import { useState, useEffect } from "react";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";

// Reusable RangeTextField component.
// Also used outside the generic table by the SOH Details sidebar range filters, so the
// min/max input pair looks and validates the same wherever a range filter appears.
export const RangeTextField = ({ label, placeholder, value, onChange, error, unit, dataTestId }) => (
    <Box sx={{ flex: 1, width: '40px' }}>
        <Typography variant="fw400s12lh24c3D4B5A" sx={{ lineHeight: '20px' }}>{label}</Typography>
        <TextField
            value={value}
            onChange={onChange}
            error={!!error}
            placeholder={placeholder}
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <Typography variant="fw400s12c3D4B5A" sx={{ lineHeight: '20px' }}>
                                {unit}
                            </Typography>
                        </InputAdornment>
                    ),
                },
                ...(dataTestId ? { htmlInput: { 'data-testid': dataTestId } } : {}),
            }}
            size="small"
            fullWidth
            sx={{
                '& .MuiOutlinedInput-root': {
                    mt: 0.5,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    border: '0.75px solid #E6E6E6',
                    transition: 'border-color 0.2s',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#BBB !important',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6D72F6 !important',
                },
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B50500 !important', // Or your custom error color
                },
                '& .MuiInputBase-input': {
                    fontSize: "12px !important",
                    fontWeight: '400 !important',
                    color: "#5D5D5D !important",
                    caretColor: "#5D5D5D !important",
                    "&::placeholder": {
                        fontWeight: 400,
                        fontSize: "12px !important",
                        color: "#9A9A9A",
                        opacity: 1,
                    },
                }
            }}
        />
    </Box>
);

const RangeFilter = ({ filter, newFilters, onChange }) => {
    // Get current values from newFilters if present

    const [minValue, setMinValue] = useState("");
    const [maxValue, setMaxValue] = useState("");
    const [error, setError] = useState("");
    const unit = filter?.unitOfMeasurement || "";

    const callOnChange = (key, values) => {
        if (!values.min && !values.max) {
            return;
        }

        values.unitOfMeasurement = unit;
        onChange(key, values);
    }

    const setupMinMaxValues = () => {
        let currentFilter = newFilters[filter.key] ?? [];
        currentFilter = currentFilter[0] ? JSON.parse(currentFilter[0]) : {};
        let min = currentFilter.start ?? ""; // Use empty string if undefined/null
        let max = currentFilter.end ?? "";   // Use empty string if undefined/null
        setMinValue(min);
        setMaxValue(max);
    }

    useEffect(() => {
        setupMinMaxValues()
    }, [filter.key, newFilters[filter.key]]);

    useEffect(() => {
        // Validate and propagate changes
        if (minValue === "" && maxValue === "") {
            setError("");
            return;
        }

        if (minValue !== "" && maxValue !== "") {
            if (Number(maxValue) <= Number(minValue)) {
                setError("Maximum value must be greater than minimum value");
            } else {
                setError("");
                callOnChange(filter.key, { min: minValue, max: maxValue });
            }
        } else {
            setError("");
            callOnChange(filter.key, { min: minValue, max: maxValue });
        }
    }, [minValue, maxValue]);



    return (
        <Box>
            <Typography variant="fw500s12lh24c5E758D" >
                Define threshold bounds
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                <RangeTextField
                    label="Minimum"
                    placeholder="Min"
                    value={minValue}
                    onChange={e => {
                        const value = e.target.value;
                        if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                            setMinValue(value);
                        }
                    }}
                    error={error}
                    unit={unit}
                />
                <RangeTextField
                    label="Maximum"
                    placeholder="Max"
                    value={maxValue}
                    onChange={e => {
                        const value = e.target.value;
                        if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                            setMaxValue(value);
                        }
                    }}
                    error={error}
                    unit={unit}
                />
            </Box>
            {error && (
                <Typography variant="fw400s10cB50500" sx={{ lineHeight: '20px' }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default RangeFilter;